import React from 'react';
import { Row, Col, Card, Statistic, Typography, Progress } from 'antd';
import { 
    DollarOutlined, 
    TrophyOutlined, 
    CreditCardOutlined, 
    RiseOutlined,
    FallOutlined
} from '@ant-design/icons';
import { PaymentStats } from '../../../../types/company.types';

const { Text } = Typography;

interface PaymentStatsCardsProps {
    stats: PaymentStats;
    loading?: boolean;
}

const PaymentStatsCards: React.FC<PaymentStatsCardsProps> = ({ stats, loading = false }) => {
    const formatCurrency = (amount: number, currency: string = 'USD') => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: currency === 'USD' ? 'USD' : 'TRY',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const getGrowthIcon = (growth: number) => {
        return growth >= 0 ? <RiseOutlined /> : <FallOutlined />;
    };

    const getGrowthColor = (growth: number) => {
        return growth >= 0 ? '#52c41a' : '#ff4d4f';
    };

    return (
        <Row gutter={[16, 16]} className="mb-4">
            <Col xs={24} sm={12} lg={6}>
                <Card 
                    className="payment-stat-card border-0 shadow-sm"
                    style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                    bodyStyle={{ padding: '20px' }}
                    loading={loading}
                >
                    <div className="text-white">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <div className="stat-icon">
                                <DollarOutlined style={{ fontSize: '28px', opacity: 0.8 }} />
                            </div>
                            <div className="text-end">
                                <div className="stat-growth">
                                    {getGrowthIcon(stats.revenueGrowth)}
                                    <span className="ms-1">{Math.abs(stats.revenueGrowth).toFixed(1)}%</span>
                                </div>
                            </div>
                        </div>
                        <div className="stat-value mb-1">
                            {formatCurrency(stats.totalRevenue)}
                        </div>
                        <div className="stat-label opacity-75">
                            Toplam Gelir
                        </div>
                        <div className="mt-2 pt-2 border-top border-white border-opacity-25">
                            <Text className="text-white opacity-75 small">
                                Bu ay: {formatCurrency(stats.monthlyRevenue)}
                            </Text>
                        </div>
                    </div>
                </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
                <Card 
                    className="payment-stat-card border-0 shadow-sm"
                    style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}
                    bodyStyle={{ padding: '20px' }}
                    loading={loading}
                >
                    <div className="text-white">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <div className="stat-icon">
                                <CreditCardOutlined style={{ fontSize: '28px', opacity: 0.8 }} />
                            </div>
                            <div className="text-end">
                                <div className="stat-badge bg-white bg-opacity-25 px-2 py-1 rounded">
                                    <span className="small">İşlemler</span>
                                </div>
                            </div>
                        </div>
                        <div className="stat-value mb-1">
                            {stats.totalTransactions.toLocaleString()}
                        </div>
                        <div className="stat-label opacity-75">
                            Toplam İşlem
                        </div>
                        <div className="mt-2 pt-2 border-top border-white border-opacity-25">
                            <Text className="text-white opacity-75 small">
                                Ortalama: {formatCurrency(stats.averageOrderValue || 0)}
                            </Text>
                        </div>
                    </div>
                </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
                <Card 
                    className="payment-stat-card border-0 shadow-sm"
                    style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}
                    bodyStyle={{ padding: '20px' }}
                    loading={loading}
                >
                    <div className="text-white">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <div className="stat-icon">
                                <TrophyOutlined style={{ fontSize: '28px', opacity: 0.8 }} />
                            </div>
                            <div className="text-end">
                                <div className="stat-progress">
                                    <Progress 
                                        type="circle" 
                                        size={40} 
                                        percent={stats.totalTransactions > 0 ? Math.round((stats.activeSubscriptions / stats.totalTransactions) * 100) : 0}
                                        strokeColor="#fff"
                                        trailColor="rgba(255,255,255,0.3)"
                                        showInfo={false}
                                        strokeWidth={8}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="stat-value mb-1">
                            {stats.activeSubscriptions.toLocaleString()}
                        </div>
                        <div className="stat-label opacity-75">
                            Aktif Abonelik
                        </div>
                        <div className="mt-2 pt-2 border-top border-white border-opacity-25">
                            <Text className="text-white opacity-75 small">
                                Premium: {stats.planTypeDistribution.premium}
                            </Text>
                        </div>
                    </div>
                </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
                <Card 
                    className="payment-stat-card border-0 shadow-sm"
                    style={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }}
                    bodyStyle={{ padding: '20px' }}
                    loading={loading}
                >
                    <div className="text-white">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <div className="stat-icon">
                                <RiseOutlined style={{ fontSize: '28px', opacity: 0.8 }} />
                            </div>
                            <div className="text-end">
                                <div className="stat-trend" style={{ color: getGrowthColor(stats.revenueGrowth) }}>
                                    <div className="bg-white bg-opacity-25 px-2 py-1 rounded">
                                        <span className="small fw-bold">
                                            {stats.revenueGrowth >= 0 ? '+' : ''}{stats.revenueGrowth.toFixed(1)}%
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="stat-value mb-1">
                            %{stats.totalTransactions > 0 ? ((stats.paymentStatusDistribution.paid / stats.totalTransactions) * 100).toFixed(1) : '0.0'}
                        </div>
                        <div className="stat-label opacity-75">
                            Başarı Oranı
                        </div>
                        <div className="mt-2 pt-2 border-top border-white border-opacity-25">
                            <Text className="text-white opacity-75 small">
                                Başarısız: {stats.paymentStatusDistribution.failed}
                            </Text>
                        </div>
                    </div>
                </Card>
            </Col>
        </Row>
    );
};

export default PaymentStatsCards; 